import React, { useState } from "react";

import { Alert, Button, Grid, Text, Loader } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

import { useForm } from "@mantine/form";
import { formatValue } from "../listings/hooks";
import { DataEntry } from "./data-entry";
import { updateDoc, doc, deleteDoc, addDoc, collection } from "firebase/firestore";
import { db } from "../../firebase";

import { AssetConfig } from "../listings/configurations";

export interface AssetEditorProps {
  asset: Record<string, any> & { id?: string };
  assetConfig: AssetConfig;
  collection: string;
  auth: {
    claims?: {
      companyId?: string;
    };
    uid?: string;
    email?: string;
  } | null | undefined;
  reloadCallback: (updatedAsset?: any) => void;
  dependencyList?: Record<string, any[]>;
  dependencyLabel?: Record<string, string>;
  useCallbackOnSave?: boolean;
}

export function AssetEditor({
  asset,
  assetConfig,
  collection: targetCollection,
  auth,
  reloadCallback,
  dependencyList,
  dependencyLabel,
  useCallbackOnSave
}: AssetEditorProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [initialValues] = useState(() => {
    const vals: Record<string, any> = { id: asset.id };
    Object.keys(assetConfig).forEach(key => {
      vals[key] = asset[key] !== undefined ? asset[key] : null;
    });
    return vals;
  });
  const [deleteCandidate, setDeleteCandidate] = useState<any | undefined>(undefined);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [messageColor, setMessageColor] = useState<string>('green');
  const [loading, setLoading] = useState<boolean>(false); // Loading state

  function buildValidationRules(assetConfig: AssetConfig) {
    let validationRulesConfig: Record<string, (value: any) => string | null> = {};
    Object.keys(assetConfig).forEach((key) => {
      // check if required (ignore booleans)
      if (assetConfig[key].required && assetConfig[key].type !== "BOOLEAN") {
        validationRulesConfig[key] = (value) => (!value ? "Required" : null);
      }
      // right now all numbers are required check if positive number
      // todo future: handle non-required number case
      if (assetConfig[key].type === "NUMBER") {
        validationRulesConfig[key] = (value) =>
          isNaN(Number(value)) || Number(value) < 0
            ? "Must be a positive number"
            : null;
      }
    });
    return validationRulesConfig;
  }

  const form = useForm({
    initialValues,
    validate: buildValidationRules(assetConfig),
  });

  async function saveAsset(updatedAsset: Record<string, any>) {
    setLoading(true); // Start loading
    if (!targetCollection || !auth?.claims?.companyId || !updatedAsset) {
      setMessageColor("red");
      setMessage("Save unsuccessful.");
      setLoading(false); // Stop loading
      return;
    }
    Object.keys(assetConfig).forEach((key) => {
      if (assetConfig[key].type === "DATE") {
        updatedAsset[key] = new Date(updatedAsset[key]).toISOString();
      }
      if (assetConfig[key].type === "BOOLEAN") {
        updatedAsset[key] = !!updatedAsset[key];
      }
      if (assetConfig[key].type === "NUMBER") {
        updatedAsset[key] = Number(updatedAsset[key]);
      }
    });

    try {
      // parent will handle save
      if (useCallbackOnSave) {
        reloadCallback(updatedAsset);
      } else {
        // handle save here
        if (updatedAsset.id) {
          // save
          const docRef = doc(db, targetCollection, updatedAsset.id);
          await updateDoc(docRef, updatedAsset);
          setMessageColor("green");
          setMessage("Successfully updated!");
          reloadCallback(updatedAsset);
        } else {
          // create
          const createdAsset: Record<string, any> = {
            ...updatedAsset,
            companyId: auth?.claims?.companyId,
          };
          delete createdAsset.id; // Remove undefined id before creating
          const docRef = await addDoc(collection(db, targetCollection), createdAsset);
          await updateDoc(docRef, { id: docRef.id });
          setMessageColor("green");
          setMessage("Successfully created!");
          reloadCallback(createdAsset);
        }
      }
    } catch (error) {
      console.log(error)
      setMessageColor("red");
      setMessage(`Save unsuccessful. ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false); // Stop loading
    }
  }

  async function deleteAsset(deleteCandidate: Record<string, any>) {
    setLoading(true); // Start loading
    try {
      const docRef = doc(db, targetCollection, deleteCandidate.id);
      await deleteDoc(docRef);
      setMessageColor("green");
      setMessage("Delete successful.");
      reloadCallback();
    } catch (error) {
      setMessageColor("red");
      setMessage(`Delete unsuccessful. ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false); // Stop loading
    }
    setDeleteCandidate(undefined);
  }

  return (
    <>
      <form onSubmit={form.onSubmit((values) => saveAsset(values))}>
        {Object.keys(initialValues)
          .filter((k) => assetConfig[k])
          .sort((a, b) => (assetConfig[a].rank ?? 0) - (assetConfig[b].rank ?? 0))
          .map((key) => (
            <div
              key={key}
              style={{
                marginTop: "1em",
              }}
            >
              <DataEntry
                label={assetConfig[key]?.label}
                edit={assetConfig[key]?.editable}
                inputProps={form.getInputProps(key as any)}
                dataType={assetConfig[key]?.type}
                form={form}
                fieldKey={key}
                selectOptions={dependencyList?.[key]}
                selectLabelKey={dependencyLabel?.[key]}
                value={formatValue(assetConfig[key]?.type, form.values[key])}
                shouldIncludeTimeForDate={false}
                decimalPrecision={assetConfig[key]?.decimalPrecision}
              />
            </div>
          ))}
        <Button
          size="md"
          color="cap-navy"
          fullWidth
          type="submit"
          mt="md"
          style={{ minHeight: 44 }}
          disabled={loading} // Disable button while loading
        >
          {loading ? <Loader size="sm" color="white" /> : "Save"}
        </Button>
        {message && <Alert color={messageColor} mt="sm">{message}</Alert>}
      </form>
      {asset.id && (
        <>
          {!!deleteCandidate ? (
            <div style={{ marginTop: 16, marginBottom: 16, border: "1px solid #ffc9c9", borderRadius: 8, padding: 12, backgroundColor: "#fff5f5" }}>
              <Text size="md" weight={500} mb="xs" align={isMobile ? "center" : "left"}>Are you sure you want to delete this asset?</Text>
              <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 12 }}>
                <Button
                  size="md"
                  color="cap-navy"
                  variant="outline"
                  fullWidth
                  onClick={() => setDeleteCandidate(undefined)}
                  disabled={loading}
                  style={{ minHeight: 44 }}
                >
                  Cancel
                </Button>
                <Button
                  size="md"
                  color="red"
                  onClick={() => deleteAsset(asset)}
                  fullWidth
                  disabled={loading}
                  style={{ minHeight: 44 }}
                >
                  {loading ? <Loader size="sm" color="white" /> : "Yes - Delete"}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              size="md"
              color="red"
              variant="outline"
              fullWidth
              mt="md"
              onClick={() => setDeleteCandidate(asset)}
              disabled={loading} // Disable button while loading
              style={{ minHeight: 44 }}
            >
              Delete
            </Button>
          )}
        </>
      )}
    </>
  );
}